import { useCallback, useMemo } from 'react';

import { useApolloClient } from '@apollo/client';

import {
  LoyaltyUserBestPromotionDocument,
  useLoyaltyUserBestPromotionQuery,
} from 'generated/graphql-gateway';
import { useSystemwidePromotionMessageQuery } from 'generated/sanity-graphql';

import { BestPromotionResult, BestPromotionSourceArgs, shouldClearActivateParam } from '../types';

// alreadyLoadedOnce prevents the Sanity skip from nulling data and flashing.
// It lives here (legacy-only) and is removed with this file at rollout end.
let alreadyLoadedOnce = false;

export function useLegacyBestPromotion({
  enabled,
  loyaltyId,
  loyaltyEnabled,
  loyaltyLoading,
  cartEntries,
  storeId,
  skipStoreDayPartHours,
  onActivateResolved,
  readMode = 'live',
}: BestPromotionSourceArgs): BestPromotionResult {
  const apolloClient = useApolloClient();

  // Shared between the live query and the post-refetch cache read so both resolve
  // the same cache key (Apollo strips undefined values consistently for each).
  const variables = useMemo(
    () => ({
      loyaltyId,
      where: {
        includePersonalized: true,
        skipStoreDayPartHours,
        cartEntries,
        // storeId is already gated on cartEntries presence by the caller; an
        // undefined value is stripped from the query variables.
        storeId,
      },
    }),
    [loyaltyId, skipStoreDayPartHours, cartEntries, storeId]
  );

  const { data } = useLoyaltyUserBestPromotionQuery({
    variables,
    skip: !enabled,
    fetchPolicy: readMode === 'cache-only' ? 'cache-only' : 'cache-and-network',
    onCompleted: completed => {
      if (shouldClearActivateParam(completed?.loyaltyUserV2?.bestPromotion)) {
        onActivateResolved?.();
      }
    },
  });

  const promotion = data?.loyaltyUserV2?.bestPromotion;
  const sanityId = promotion?.sanityId;

  // alreadyLoadedOnce is a hack to prevent skipping the query which makes it
  // null and flashing occurs.
  const { data: sanityData } = useSystemwidePromotionMessageQuery({
    variables: { id: sanityId ?? '' },
    skip: !enabled || !sanityId || (!alreadyLoadedOnce && (!loyaltyEnabled || loyaltyLoading)),
    fetchPolicy: readMode === 'cache-only' ? 'cache-only' : 'cache-and-network',
  });

  if (sanityData) {
    alreadyLoadedOnce = true;
  }

  const refetchPromotion = useCallback(async () => {
    await apolloClient.refetchQueries({ include: [LoyaltyUserBestPromotionDocument] });
    const rereadPromotion = apolloClient.readQuery({
      query: LoyaltyUserBestPromotionDocument,
      variables,
    })?.loyaltyUserV2?.bestPromotion;
    return {
      promotionId: rereadPromotion?.id,
      mustActivate: rereadPromotion?.mustActivate,
      userActivatedAt: rereadPromotion?.userActivatedAt,
    };
  }, [apolloClient, variables]);

  const sanityPromotion = sanityData?.SystemwidePromotion;

  return {
    promotionId: promotion?.id,
    mustActivate: promotion?.mustActivate,
    userActivatedAt: promotion?.userActivatedAt,
    content: sanityPromotion
      ? {
          name: sanityPromotion.name,
          loyaltyEngineId: sanityPromotion.loyaltyEngineId,
          promoMessage: sanityPromotion.promoMessage?.locale,
          promoMessageBlock: sanityPromotion.promoMessageBlock?.locale,
          activatedPromoMessageBlock: sanityPromotion.activatedPromoMessageBlock?.locale,
        }
      : null,
    refetchPromotion,
  };
}
